'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import React, { useEffect, useState } from 'react';
import { FormGroup, Input, Label, Button, InputGroup, Col } from 'reactstrap';
import { MdClose, MdSearch, MdOutlineLocationOn } from 'react-icons/md';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { ApiLocation, getLocationsForAutocomplete } from '../api/locations';
import { plausibleEvent } from '../utils/plausibleEvent';
import { getI18nUtils } from '../i18n/i18n-utils';
import { useSearchParams } from 'next/navigation';
import { Dictionaries } from '../i18n/i18n-config';

type JobSearchFormSubmitBody = { radius: string | null; what: string | null; where: string | null };

const sharedCustomStyles = {
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? 'white' : 'black',
    background: state.isSelected ? '#e2001a' : 'white',
    '&:hover': {
      background: '#e9ecef',
    },
  }),
  placeholder: (base) => ({
    ...base,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '@media only screen and (min-width: 992px)': {
      color: 'transparent',
    },
  }),
};

const customLocationStyles = {
  ...sharedCustomStyles,
  control: (base) => ({
    ...base,
    border: 'none',
    borderRight: '#ced4da 1px solid',
    borderTopRightRadius: '0',
    borderBottomRightRadius: '0',
    boxShadow: 'none',
    padding: '0 24px 0 30px',
    '&:hover': {
      borderRight: '#ced4da 1px solid',
    },
  }),
};

const customRadiusStyles = {
  ...sharedCustomStyles,
  control: (base) => ({
    ...base,
    border: 'none',
    borderLeft: '#ced4da 1px solid',
    borderTopLeftRadius: '0',
    borderBottomLeftRadius: '0',
    boxShadow: 'none',
    '&:hover': {
      borderLeft: '#ced4da 1px solid',
    },
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'black',
    '&:hover': {
      color: 'black',
    },
  }),
};

const radiusOptions = [
  { label: '5km', value: '5' },
  { label: '10km', value: '10' },
  { label: '25km', value: '25' },
  { label: '50km', value: '50' },
  { label: '75km', value: '75' },
  { label: '100km', value: '100' },
];

function JobSearchForm({
  dictionary,
  formSubmitHandler,
}: {
  dictionary: Partial<Dictionaries>;
  formSubmitHandler: (submitBody: JobSearchFormSubmitBody) => void;
}): JSX.Element {
  const { formatMessage } = getI18nUtils(dictionary);
  const searchParams = useSearchParams();
  const whereParam = searchParams.get('where');
  let initialLocation: Partial<ApiLocation> | null = null;
  if (whereParam) {
    const [postalCode, city] = whereParam.split('!');
    initialLocation = { postalCode, city };
  }

  const [inputs, setInputs] = useState<{ what: string; location: Partial<ApiLocation> | null; radius: string }>({
    what: searchParams.get('what') || '',
    location: initialLocation,
    radius: searchParams.get('radius') || '25',
  });

  const { what, location, radius } = inputs;

  // make the container sticky if there is sufficient height
  useEffect(() => {
    const jobSearchContainer = document.getElementById('jobsearchContainer');
    const jobSearchHeight = jobSearchContainer?.offsetHeight;
    const windowHeight = window.innerHeight;
    const isMobile = window.innerWidth <= 768;

    if (jobSearchHeight && windowHeight - 40 > jobSearchHeight && !isMobile) jobSearchContainer.classList.add('sticky');
    else jobSearchContainer?.classList.remove('sticky');
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    plausibleEvent('jobSearch');

    const body: JobSearchFormSubmitBody = {
      radius: radius || null,
      what: what || null,
      where: location ? `${location.postalCode}!${location.city}` : null,
    };

    formSubmitHandler(body);
  };

  return (
    <div id="jobSearchForm" className="job-search-form card bg-primary text-white p-4 mb-0">
      <h1 className="mb-4 mb-lg-2" style={{ textTransform: 'none' }}>
        {formatMessage({ id: 'index-h1' })}
      </h1>

      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="what" className="d-none border-0 d-lg-block">
            {formatMessage({ id: 'index-search-block-what-label' })}
          </Label>
          <InputGroup>
            <Input
              style={{ zIndex: 3 }}
              type="text"
              name="what"
              id="what"
              placeholder={formatMessage({ id: 'index-search-block-what-label' })}
              value={what}
              onChange={(e) => setInputs((prev) => ({ ...prev, what: e.target.value }))}
              maxLength={255}
            />
            <MdSearch className="search-field-icon" size="24" />
            {what && (
              <Button className="btn-clear" color="text-primary" onClick={() => setInputs((prev) => ({ ...prev, what: '' }))}>
                <MdClose />
              </Button>
            )}
          </InputGroup>
        </FormGroup>
        <FormGroup>
          <Label for="where" className="border-0 d-none d-lg-block">
            {formatMessage({ id: 'index-search-block-where-label' })}
          </Label>
          <InputGroup>
            <Col xs={8}>
              <AsyncSelect
                instanceId="where"
                components={{ DropdownIndicator: null }}
                name="where"
                id="where"
                placeholder={formatMessage({ id: 'index-search-block-where-label' })}
                value={location}
                loadOptions={getLocationsForAutocomplete}
                styles={customLocationStyles}
                defaultOptions
                className="location-select notranslate"
                getOptionLabel={(option: any) => `${option?.postalCode} ${option?.city}`}
                getOptionValue={(option: any) => `${option?.postalCode} ${option?.city}`}
                onChange={(selected) => setInputs((prev) => ({ ...prev, location: selected }))}
              />
              <MdOutlineLocationOn className="search-field-icon" size="24" />
              {location && (
                <Button
                  className="btn-clear"
                  color="text-primary"
                  onClick={() => setInputs((prev) => ({ ...prev, location: null }))}
                >
                  <MdClose />
                </Button>
              )}
            </Col>
            <Col xs={4}>
              <Select
                instanceId="radius"
                components={{ IndicatorSeparator: null }}
                name="radius"
                id="radius"
                className="radius-select"
                value={radiusOptions.find((o) => o.value === radius)}
                onChange={(selected) => {
                  setInputs((prev) => {
                    if (selected?.value === undefined) return prev;
                    return {
                      ...prev,
                      radius: selected.value,
                    };
                  });
                }}
                options={radiusOptions}
                styles={customRadiusStyles}
              />
            </Col>
          </InputGroup>
        </FormGroup>
        <Button
          type="submit"
          color="primary"
          className="d-flex align-items-center justify-content-center btn-outlined border-white w-100"
        >
          <MdSearch className="me-2" size="20" />
          {formatMessage({ id: 'jobs-search-action' })}
        </Button>
      </form>
    </div>
  );
}

export default JobSearchForm;
